import { useEffect, useRef, useState, type InputHTMLAttributes } from 'react';
import type { Client } from '../data/types';
import { searchAddress, type GeocodeResult } from '../lib/geo';
import { isOnline } from '../sync/connectivity';
import { useDismiss } from './useDismiss';

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>;

/** Client name input with suggestions from the client register. */
export function ClientLookup({ value, onChange, onPick, clients, ...props }: InputProps & { value: string; onChange(value: string): void; onPick(client: Client): void; clients: Client[] }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const term = value.trim().toLowerCase();
  const matches = term ? clients.filter((client) => `${client.name} ${client.contact} ${client.address}`.toLowerCase().includes(term)).slice(0, 7) : [];
  const showMenu = open && matches.length > 0 && !(matches.length === 1 && matches[0].name === value);
  useDismiss(showMenu, [wrapRef], () => setOpen(false));
  return (
    <div className="lookup-wrap" ref={wrapRef}>
      <input {...props} value={value} autoComplete="off" onChange={(event) => { onChange(event.target.value); setOpen(true); }} onFocus={() => setOpen(true)} />
      {showMenu && (
        <div className="lookup-menu">
          {matches.map((client) => (
            <button key={client.id} className="lookup-item" type="button" onClick={() => { onPick(client); setOpen(false); }}>
              <b>{client.name}</b><small>{client.contact} · {client.address}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Address input with OpenStreetMap suggestions (online only; typing still works offline). */
export function AddressLookup({ value, onChange, onPick, ...props }: InputProps & { value: string; onChange(value: string): void; onPick?(result: GeocodeResult): void }) {
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  useDismiss(open && results.length > 0, [wrapRef], () => setOpen(false));
  useEffect(() => {
    const query = value.trim();
    if (!typed || query.length < 4 || !isOnline()) { setResults([]); return; }
    let cancelled = false;
    const timer = setTimeout(() => {
      searchAddress(query, 5).then((found) => { if (!cancelled) { setResults(found); setOpen(true); } }).catch(() => !cancelled && setResults([]));
    }, 450);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [value, typed]);
  return (
    <div className="lookup-wrap" ref={wrapRef}>
      <input {...props} value={value} autoComplete="off" onChange={(event) => { onChange(event.target.value); setTyped(true); }} />
      {open && results.length > 0 && (
        <div className="lookup-menu">
          {results.map((result) => (
            <button key={result.displayName} className="lookup-item" type="button" onClick={() => { onChange(result.displayName); setTyped(false); setOpen(false); onPick?.(result); }}>
              <b>{result.displayName}</b><small>Verified map result</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
